import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { CreditCard, Crown, CheckCircle, Lightning, Users, Folder, Truck, ArrowRight, Receipt, Clock, ShieldCheck, Rocket, Buildings } from "@phosphor-icons/react";

const PLAN_ICONS = { starter: Rocket, professional: Lightning, enterprise: Crown, one_time_licence: ShieldCheck };
const PLAN_COLORS = {
  starter: { bg: "bg-blue-50 dark:bg-blue-950/20", border: "border-blue-200 dark:border-blue-800", accent: "text-blue-600", btn: "bg-blue-600 hover:bg-blue-700" },
  professional: { bg: "bg-purple-50 dark:bg-purple-950/20", border: "border-purple-200 dark:border-purple-800", accent: "text-purple-600", btn: "bg-purple-600 hover:bg-purple-700" },
  enterprise: { bg: "bg-amber-50 dark:bg-amber-950/20", border: "border-amber-200 dark:border-amber-800", accent: "text-amber-600", btn: "bg-amber-600 hover:bg-amber-700" },
  one_time_licence: { bg: "bg-emerald-50 dark:bg-emerald-950/20", border: "border-emerald-200 dark:border-emerald-800", accent: "text-emerald-600", btn: "bg-emerald-600 hover:bg-emerald-700" },
};

function UsageItem({ icon: Icon, label, used, limit }) {
  const unlimited = limit === -1;
  const pct = unlimited ? 0 : limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const isWarning = !unlimited && pct >= 80;
  return (
    <div className="flex items-center gap-3">
      <Icon size={16} className="text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex justify-between text-xs mb-0.5">
          <span className="text-muted-foreground">{label}</span>
          <span className={`font-semibold ${isWarning ? "text-amber-600" : ""}`}>{used}{unlimited ? " (unlimited)" : ` / ${limit}`}</span>
        </div>
        {!unlimited && <Progress value={pct} className="h-1.5" />}
      </div>
    </div>
  );
}

export default function BillingPage() {
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [searchParams] = useSearchParams();

  useEffect(() => {
    Promise.all([
      API.get("/billing/plans").then(({ data }) => setPlans(data)),
      API.get("/billing/current").then(({ data }) => setBilling(data)),
      API.get("/billing/transactions").then(({ data }) => setTransactions(data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Poll payment status on return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 5) return;
      attempts++;
      try {
        const { data } = await API.get(`/billing/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Payment successful! Your plan has been upgraded.");
          // Refresh billing data
          API.get("/billing/current").then(({ data }) => setBilling(data));
          API.get("/billing/transactions").then(({ data }) => setTransactions(data));
          return;
        }
        if (data.status === "expired") {
          toast.error("Payment session expired. Please try again.");
          return;
        }
        setTimeout(poll, 2000);
      } catch { setTimeout(poll, 2000); }
    };
    poll();
  }, [searchParams]);

  const handleCheckout = async (planId) => {
    setCheckingOut(planId);
    try {
      const cycle = planId === "one_time_licence" ? "one_time" : billingCycle;
      const { data } = await API.post("/billing/checkout", {
        plan_id: planId,
        billing_cycle: cycle,
        origin_url: window.location.origin,
      });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
    } finally { setCheckingOut(null); }
  };

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading billing...</div>;

  const currentPlan = billing?.plan_id || "starter";

  return (
    <div data-testid="billing-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center">
          <CreditCard size={18} className="text-white" weight="fill" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Billing & Subscription</h1>
          <p className="text-xs text-muted-foreground">Manage your plan, usage, and payment history</p>
        </div>
      </div>

      {/* Current Plan Summary */}
      {billing && (
        <div className={`border-2 rounded-lg p-5 mb-6 ${PLAN_COLORS[currentPlan]?.border || "border-border"} ${PLAN_COLORS[currentPlan]?.bg || "bg-card"}`} data-testid="current-plan-card">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              {(() => { const Icon = PLAN_ICONS[currentPlan] || Crown; return <Icon size={22} className={PLAN_COLORS[currentPlan]?.accent || "text-primary"} weight="fill" />; })()}
              <div>
                <h2 className="text-base font-bold font-[Chivo]">{billing.plan_name} Plan</h2>
                <div className="flex items-center gap-2 mt-0.5">
                  <Badge className={`text-[9px] ${billing.subscription_status === "active" ? "bg-emerald-100 text-emerald-700" : billing.subscription_status === "trialing" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>
                    {billing.subscription_status === "trialing" ? "Free Trial" : billing.subscription_status}
                  </Badge>
                  {billing.subscription_status === "trialing" && billing.trial_ends_at && (
                    <span className="text-xs text-blue-600 font-medium flex items-center gap-1">
                      <Clock size={12} />
                      {Math.max(0, Math.ceil((new Date(billing.trial_ends_at) - new Date()) / 86400000))} days remaining
                    </span>
                  )}
                  {billing.monthly_price && <span className="text-xs text-muted-foreground">£{billing.monthly_price}/mo</span>}
                  {billing.one_time_price && <span className="text-xs text-muted-foreground">£{billing.one_time_price} one-time</span>}
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <UsageItem icon={Users} label="Users" used={billing.usage?.users || 0} limit={billing.plan_limits?.users || -1} />
            <UsageItem icon={Folder} label="Projects" used={billing.usage?.projects || 0} limit={billing.plan_limits?.projects || -1} />
            <UsageItem icon={Truck} label="Suppliers" used={billing.usage?.suppliers || 0} limit={billing.plan_limits?.suppliers || -1} />
          </div>
        </div>
      )}

      {/* Billing Cycle Toggle */}
      <div className="flex items-center justify-center gap-2 mb-6">
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1" data-testid="billing-cycle-toggle">
          <button onClick={() => setBillingCycle("monthly")} className={`px-4 py-1.5 rounded-md text-xs font-medium transition-all ${billingCycle === "monthly" ? "bg-background shadow text-foreground" : "text-muted-foreground"}`} data-testid="cycle-monthly">Monthly</button>
          <button onClick={() => setBillingCycle("annual")} className={`px-4 py-1.5 rounded-md text-xs font-medium transition-all ${billingCycle === "annual" ? "bg-background shadow text-foreground" : "text-muted-foreground"}`} data-testid="cycle-annual">
            Annual <span className="text-emerald-600 font-bold ml-1">Save 17%</span>
          </button>
        </div>
      </div>

      {/* Plan Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="plan-cards">
        {plans.map(plan => {
          const colors = PLAN_COLORS[plan.id] || PLAN_COLORS.starter;
          const Icon = PLAN_ICONS[plan.id] || Rocket;
          const isCurrent = currentPlan === plan.id;
          const price = plan.id === "one_time_licence"
            ? plan.one_time_price
            : billingCycle === "annual" ? plan.annual_price : plan.monthly_price;
          const priceLabel = plan.id === "one_time_licence" ? "one-time" : billingCycle === "annual" ? "/year" : "/month";

          return (
            <div key={plan.id} className={`border-2 rounded-lg overflow-hidden transition-all ${isCurrent ? `${colors.border} ${colors.bg} ring-2 ring-offset-2 ring-primary/20` : "border-border bg-card hover:border-border/80"}`} data-testid={`plan-${plan.id}`}>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={18} className={colors.accent} weight="fill" />
                  <h3 className="text-sm font-bold font-[Chivo]">{plan.name}</h3>
                  {isCurrent && <Badge className="text-[8px] bg-primary text-primary-foreground ml-auto">Current</Badge>}
                </div>
                <div className="mb-3">
                  <span className="text-2xl font-bold font-[Chivo]">£{price}</span>
                  <span className="text-xs text-muted-foreground ml-1">{priceLabel}</span>
                </div>
                <ul className="space-y-1.5 mb-4">
                  {plan.features.map((f, i) => (
                    <li key={i} className="text-[11px] text-muted-foreground flex items-start gap-1.5">
                      <CheckCircle size={12} className="text-emerald-500 shrink-0 mt-0.5" weight="fill" />
                      {f}
                    </li>
                  ))}
                </ul>
                {isCurrent ? (
                  <Button variant="outline" size="sm" className="w-full text-xs" disabled>Current Plan</Button>
                ) : (
                  <Button
                    size="sm"
                    className={`w-full text-xs text-white ${colors.btn}`}
                    onClick={() => handleCheckout(plan.id)}
                    disabled={!!checkingOut}
                    data-testid={`checkout-${plan.id}`}
                  >
                    {checkingOut === plan.id ? "Processing..." : isCurrent ? "Current" : plan.id === "one_time_licence" ? "Purchase Licence" : "Upgrade"}
                    {checkingOut !== plan.id && <ArrowRight size={12} className="ml-1" />}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Payment History */}
      <div className="border border-border rounded-lg overflow-hidden" data-testid="payment-history">
        <div className="px-4 py-3 bg-muted/30 border-b border-border flex items-center gap-2">
          <Receipt size={14} className="text-muted-foreground" />
          <h3 className="text-sm font-semibold font-[Chivo]">Payment History</h3>
        </div>
        {transactions.length === 0 ? (
          <p className="px-4 py-8 text-xs text-muted-foreground text-center">No payments yet</p>
        ) : (
          <div className="divide-y divide-border">
            {transactions.map(tx => (
              <div key={tx.id} className="flex items-center justify-between px-4 py-3" data-testid={`tx-${tx.id}`}>
                <div>
                  <p className="text-xs font-semibold">{PLAN_ICONS[tx.plan_id] ? tx.plan_id.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase()) : tx.plan_id} — {tx.billing_cycle}</p>
                  <p className="text-[10px] text-muted-foreground flex items-center gap-1"><Clock size={10} /> {new Date(tx.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold font-[Chivo]">£{tx.amount?.toFixed(2)}</p>
                  <Badge className={`text-[8px] ${tx.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : tx.payment_status === "initiated" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                    {tx.payment_status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
